<template>
  <div>
    <div class="section-header">
      <h2 class="section-title">Researcher Management</h2>
      <Button
        @click="showCreateDialog = true"
        variant="default"
        size="sm"
        class="refresh-btn"
      >
        Add Researcher
      </Button>
    </div>

    <div v-if="loading" class="loading-state">
      Loading researchers...
    </div>

    <div v-else-if="error" class="message error">
      {{ error }}
      <Button variant="default" size="sm" @click="fetchResearchers" style="margin-left: 0.5rem;">Try again</Button>
    </div>

    <div v-else class="researchers-list">
      <div v-if="researchers.length === 0" class="empty-state">
        No researchers found.
      </div>

      <div v-else class="researcher-items">
        <div
          v-for="user in researchers"
          :key="user.id"
          class="researcher-item"
        >
          <div class="researcher-header" @click="toggleResearcher(user.id)" style="cursor: pointer;">
            <div class="researcher-info">
              <div class="researcher-name">{{ user.name }}</div>
            </div>
            <div class="researcher-status-group">
              <span :class="[
                'status-badge',
                user.is_complete ? 'status-complete' : 'status-pending'
              ]">
                {{ formatStatus(user.registration_status) }}
              </span>
              <span class="expand-icon" :class="{ expanded: expandedResearchers[user.id] }">
                ▼
              </span>
            </div>
          </div>

          <div v-if="expandedResearchers[user.id]" class="researcher-details">
            <div class="detail-row">
              <span class="detail-label">Email:</span>
              <span class="detail-value">{{ user.email }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Phone:</span>
              <span class="detail-value">{{ user.phone }}</span>
            </div>
            <div v-if="user.address" class="detail-row">
              <span class="detail-label">Address:</span>
              <span class="detail-value">{{ user.address }}</span>
            </div>

            <div class="researcher-studies">
              <strong>Assigned Studies:</strong>
              <div class="studies-tags">
                <span
                  v-for="study in user.studies"
                  :key="study.id"
                  class="study-tag"
                >
                  {{ study.name }}
                </span>
                <span v-if="!user.studies || user.studies.length === 0" class="no-studies">None</span>
              </div>
            </div>

            <div class="researcher-actions">
              <Button
                variant="default"
                size="sm"
                @click.stop="manageStudies(user)"
                style="background-color: #3b82f6;"
              >
                Assign Studies
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Researcher Dialog -->
    <div v-if="showCreateDialog" class="modal-overlay" @click.self="showCreateDialog = false">
      <div class="modal-content">
        <h3 class="form-title">Invite Researcher</h3>
        <form @submit.prevent="handleInvite" class="researcher-form">
          <div class="form-group">
            <Label for="researcher-name">Name *</Label>
            <Input
              id="researcher-name"
              v-model="inviteForm.name"
              required
              placeholder="Dr. John Doe"
            />
          </div>
          <div class="form-group">
            <Label for="researcher-email">Email *</Label>
            <Input
              id="researcher-email"
              v-model="inviteForm.email"
              type="email"
              required
              placeholder="john.doe@example.com"
            />
          </div>
          <div class="form-group">
            <Label for="researcher-phone">Phone *</Label>
            <Input
              id="researcher-phone"
              v-model="inviteForm.phone"
              type="tel"
              required
              placeholder="+1234567890"
            />
            <p class="field-hint">E.164 format (e.g. +1234567890)</p>
          </div>
          <div class="form-group">
            <Label for="researcher-address">Address (Optional)</Label>
            <Input
              id="researcher-address"
              v-model="inviteForm.address"
              placeholder="123 Medical Center Dr"
            />
          </div>

          <div v-if="inviteError" class="message error">
            {{ inviteError }}
          </div>

          <div class="form-actions">
            <Button
              type="submit"
              :disabled="inviteLoading"
              variant="default"
              size="sm"
              class="submit-btn"
            >
              <span v-if="inviteLoading">Sending Invite...</span>
              <span v-else>Send Invitation</span>
            </Button>
            <Button
              type="button"
              @click="showCreateDialog = false"
              :disabled="inviteLoading"
              variant="default"
              size="sm"
              class="cancel-btn"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>

    <!-- Manage Studies Modal -->
    <div v-if="showStudyModal" class="modal-overlay" @click.self="showStudyModal = false">
      <div class="modal-content study-modal">
        <h3 class="form-title">Assign Studies to {{ managingUser?.name }}</h3>

        <div v-if="studyLoading" class="loading-state">
          Loading available studies...
        </div>

        <div v-else-if="studyError" class="message error">
          {{ studyError }}
          <Button variant="default" size="sm" @click="fetchAvailableStudies" style="margin-left: 0.5rem;">Try again</Button>
        </div>

        <div v-else class="studies-selection">
          <p class="selection-hint">Select studies this researcher should have access to:</p>
          <div class="study-search-bar">
            <Input
              v-model="studySearchQuery"
              placeholder="Search studies (incl. ClinicalTrials.gov)"
              @keyup.enter="searchStudies"
            />
            <Button variant="default" size="sm" @click="searchStudies" :disabled="studySearching">
              <span v-if="!studySearching">Search</span>
              <span v-else>Searching...</span>
            </Button>
          </div>
          <div class="studies-list-container" ref="studiesListEl" @scroll="onStudiesScroll">
            <div v-if="displayedStudies.length === 0" class="empty-state">
              No studies found in the system.
            </div>
            <div
              v-for="study in displayedStudies"
              :key="study.id"
              class="study-checkbox-item"
            >
              <input
                type="checkbox"
                :id="'study-' + study.id"
                :value="study.id"
                v-model="selectedStudyIds"
              />
              <label :for="'study-' + study.id">
                <span class="study-name">
                  {{ study.name }}
                  <span v-if="study.is_bulk_imported" class="study-source-tag">ClinicalTrials.gov</span>
                </span>
                <span class="study-description">{{ study.description }}</span>
              </label>
            </div>
            <div v-if="studyLoadingMore" class="studies-load-more">Loading more studies...</div>
            <div v-else-if="!studyHasMore && availableStudies.length > 0" class="studies-load-more end">No more studies</div>
          </div>
        </div>

        <div class="form-actions">
          <Button
            @click="handleSaveStudies"
            :disabled="saveLoading || studyLoading"
            variant="default"
            size="sm"
            class="submit-btn"
          >
            <span v-if="saveLoading">Saving...</span>
            <span v-else>Save Changes</span>
          </Button>
          <Button
            type="button"
            @click="showStudyModal = false"
            :disabled="saveLoading"
            variant="default"
            size="sm"
            class="cancel-btn"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive, computed } from 'vue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import researcherApi from '@/api/researcher'

const researchers = ref([])
const loading = ref(true)
const error = ref(null)
const showCreateDialog = ref(false)
const expandedResearchers = reactive({})

// Study Management State
const showStudyModal = ref(false)
const availableStudies = ref([])
const selectedStudyIds = ref([])
const originalStudyIds = ref([])
const managingUser = ref(null)
const studyLoading = ref(false)
const studyError = ref('')
const saveLoading = ref(false)
// Search + already-assigned handling so any study (including bulk
// ClinicalTrials.gov-synced ones) can be found and assigned. availableStudies
// holds the current search page; assignedStudies keeps the researcher's current
// assignments visible even when they aren't in the search results, so they stay
// checked and are not dropped on save.
const studySearchQuery = ref('')
const studySearching = ref(false)
const assignedStudies = ref([])
// Infinite-scroll pagination for the study picker: the list grows as the admin
// scrolls instead of being capped, so any of the (potentially tens of
// thousands of) bulk studies can be reached.
const STUDY_PAGE_SIZE = 50
const studyOffset = ref(0)
const studyHasMore = ref(false)
const studyLoadingMore = ref(false)
const studiesListEl = ref(null)

// Merge assigned studies (first) with search results, de-duplicated by id.
const displayedStudies = computed(() => {
  const seen = new Set()
  const merged = []
  for (const study of [...assignedStudies.value, ...availableStudies.value]) {
    if (study && study.id != null && !seen.has(study.id)) {
      seen.add(study.id)
      merged.push(study)
    }
  }
  return merged
})

const toggleResearcher = (id) => {
  expandedResearchers[id] = !expandedResearchers[id]
}

const fetchAvailableStudies = async ({ append = false } = {}) => {
  if (append) {
    studyLoadingMore.value = true
  } else {
    studyLoading.value = true
    studyOffset.value = 0
    availableStudies.value = []
  }
  studyError.value = ''
  try {
    // include_bulk so bulk ClinicalTrials.gov-synced studies are assignable.
    // Results are paged (auto-loaded on scroll) rather than capped.
    const params = {
      include_bulk: true,
      limit: STUDY_PAGE_SIZE,
      offset: studyOffset.value,
    }
    const trimmed = studySearchQuery.value && studySearchQuery.value.trim()
    if (trimmed) {
      params.search = trimmed
    }
    const res = await researcherApi.getAllStudies(params)
    const page = res || []
    availableStudies.value = append ? [...availableStudies.value, ...page] : page
    // A full page implies there may be more rows to fetch.
    studyHasMore.value = page.length === STUDY_PAGE_SIZE
  } catch (err) {
    console.error(err)
    studyError.value = "Failed to load available studies."
  } finally {
    studyLoading.value = false
    studyLoadingMore.value = false
  }
}

const loadMoreStudies = async () => {
  if (!studyHasMore.value || studyLoadingMore.value || studyLoading.value) {
    return
  }
  studyOffset.value += STUDY_PAGE_SIZE
  await fetchAvailableStudies({ append: true })
}

const onStudiesScroll = (event) => {
  const el = event.target
  if (!el) return
  const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight)
  if (distanceFromBottom <= 80) {
    loadMoreStudies()
  }
}

const searchStudies = async () => {
  studySearching.value = true
  try {
    // Reset paging for the new query.
    studyOffset.value = 0
    if (studiesListEl.value) {
      studiesListEl.value.scrollTop = 0
    }
    await fetchAvailableStudies()
  } finally {
    studySearching.value = false
  }
}


const fetchResearchers = async () => {
  loading.value = true
  error.value = null
  try {
    // Attempt real call:
    const res = await researcherApi.getAllResearchers()
    researchers.value = res || []

    // Fallback or empty if needed, but primarily relying on API now.
    if (!researchers.value.length) {
       // Optional: Leave empty or handle empty state
    }

  } catch (err) {
    error.value = "Failed to load researchers."
    console.error(err)
  } finally {
    loading.value = false
  }
}

const formatStatus = (status) => {
  if (!status) return 'Unknown'
  return status.replace('_', ' ')
}

const inviteForm = reactive({
  name: '',
  email: '',
  phone: '',
  address: ''
})
const inviteLoading = ref(false)
const inviteError = ref('')

const handleInvite = async () => {
  inviteLoading.value = true
  inviteError.value = ''
  try {
    await researcherApi.invite({
      name: inviteForm.name,
      email: inviteForm.email,
      phone: inviteForm.phone,
      address: inviteForm.address
    })

    // Success
    showCreateDialog.value = false
    // Reset form
    Object.assign(inviteForm, { name: '', email: '', phone: '', address: '' })
    // Refresh list
    await fetchResearchers()

  } catch (err) {
    console.error(err)
    inviteError.value = err.response?.data?.detail || "Failed to invite researcher"
  } finally {
    inviteLoading.value = false
  }
}

const manageStudies = async (user) => {
  managingUser.value = user
  showStudyModal.value = true
  studyError.value = ''

  // Set initially selected studies
  const currentStudies = user.studies ? user.studies.map(s => s.id) : []
  selectedStudyIds.value = [...currentStudies]
  originalStudyIds.value = [...currentStudies]

  // Keep the researcher's currently assigned studies visible (and checked) even
  // if they don't appear in the initial/search results.
  assignedStudies.value = user.studies ? [...user.studies] : []

  // Always reload so bulk studies are included and results reflect the current
  // (reset) search box.
  studySearchQuery.value = ''
  await fetchAvailableStudies()
}

const handleSaveStudies = async () => {
  if (!managingUser.value) return

  saveLoading.value = true
  studyError.value = ''
  try {
    const researcherId = managingUser.value.id
    
    // Determine which studies to add and remove
    const studiesToAdd = selectedStudyIds.value.filter(id => !originalStudyIds.value.includes(id))
    const studiesToRemove = originalStudyIds.value.filter(id => !selectedStudyIds.value.includes(id))

    // Remove deselected studies
    for (const studyId of studiesToRemove) {
      await researcherApi.removeStudy(researcherId, studyId)
    }

    // Add newly selected studies (only if there are new ones)
    if (studiesToAdd.length > 0) {
      await researcherApi.assignStudies(researcherId, studiesToAdd)
    }

    // Refresh the researcher list to show updated studies
    await fetchResearchers()

    showStudyModal.value = false
  } catch (err) {
    console.error(err)
    studyError.value = err.response?.data?.detail || "Failed to update study assignments"
  } finally {
    saveLoading.value = false
  }
}

onMounted(() => {
  fetchResearchers()
})
</script>

<style scoped>
/* Section Header - matching Admin.vue */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.refresh-btn {
  background-color: #3b82f6;
  color: #ffffff;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.refresh-btn:hover:not(:disabled) {
  background-color: #2563eb;
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Loading and Empty States */
.loading-state,
.empty-state {
  padding: 2rem;
  text-align: center;
  color: #64748b;
}

/* Message Styles */
.message {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.9375rem;
}

.message.error {
  background-color: #fee2e2;
  color: #991b1b;
  border: 1px solid #ef4444;
}


/* Researchers List */
.researchers-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.researcher-items {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.researcher-item {
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  overflow: hidden;
  background-color: #ffffff;
  transition: all 0.2s ease;
}

.researcher-item:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.researcher-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
}

.researcher-info {
  flex: 1;
}

.researcher-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
}

.researcher-status-group {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.status-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-weight: 500;
  text-transform: uppercase;
  font-size: 0.75rem;
}

.status-complete {
  background-color: #d1fae5;
  color: #065f46;
}

.status-pending {
  background-color: #fef3c7;
  color: #92400e;
}

.expand-icon {
  font-size: 0.75rem;
  color: #64748b;
  transition: transform 0.2s ease;
}

.expand-icon.expanded {
  transform: rotate(180deg);
}

.researcher-details {
  border-top: 1px solid #e2e8f0;
  padding: 1rem;
  background-color: #f8fafc;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.detail-row {
  display: flex;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.detail-label {
  font-weight: 600;
  color: #64748b;
  width: 80px;
}

.detail-value {
  color: #1e293b;
}

.researcher-studies {
  padding: 0.75rem;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
}

.researcher-studies strong {
  display: block;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.studies-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.study-tag {
  padding: 0.25rem 0.5rem;
  background-color: #e0e7ff;
  color: #4338ca;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 500;
}

.no-studies {
  color: #94a3b8;
  font-style: italic;
  font-size: 0.875rem;
}

.researcher-actions {
  display: flex;
  justify-content: flex-end;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-content {
  background-color: #ffffff;
  padding: 1.5rem;
  border-radius: 0.5rem;
  max-width: 28rem;
  width: 100%;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.form-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1rem;
}

.researcher-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.field-hint {
  font-size: 0.75rem;
  color: #64748b;
  margin-top: 0.25rem;
}

.form-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.submit-btn {
  background-color: #3b82f6;
  color: #ffffff;
}

.submit-btn:hover:not(:disabled) {
  background-color: #2563eb;
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cancel-btn {
  background-color: #64748b;
  color: #ffffff;
}

.cancel-btn:hover:not(:disabled) {
  background-color: #475569;
}

.cancel-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Responsive Design */
@media (max-width: 768px) {
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .researcher-header {
    flex-direction: column;
    gap: 0.5rem;
  }

  .researcher-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .form-actions {
    flex-direction: column;
  }

  .modal-content {
    margin: 1rem;
  }
}

/* Study Modal Specific Styles */
.study-modal {
  max-width: 32rem;
}

.studies-selection {
  margin-bottom: 1.5rem;
}

.selection-hint {
  font-size: 0.875rem;
  color: #64748b;
  margin-bottom: 1rem;
}

.study-search-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.study-search-bar :deep(input) {
  flex: 1;
}

.study-source-tag {
  display: inline-block;
  margin-left: 0.5rem;
  padding: 0.05rem 0.4rem;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #1d4ed8;
  background-color: #dbeafe;
  border-radius: 0.25rem;
  vertical-align: middle;
}

.studies-load-more {
  padding: 0.75rem;
  text-align: center;
  font-size: 0.8125rem;
  color: #64748b;
  font-style: italic;
}

.studies-load-more.end {
  color: #94a3b8;
  font-style: normal;
}

.studies-list-container {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  padding: 0.5rem;
  background-color: #f8fafc;
}

.study-checkbox-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  border-bottom: 1px solid #e2e8f0;
  cursor: pointer;
  transition: background-color 0.2s;
}

.study-checkbox-item:last-child {
  border-bottom: none;
}

.study-checkbox-item:hover {
  background-color: #f1f5f9;
}

.study-checkbox-item input[type="checkbox"] {
  margin-top: 0.25rem;
  cursor: pointer;
}

.study-checkbox-item label {
  cursor: pointer;
  display: flex;
  flex-direction: column;
}

.study-name {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1e293b;
}

.study-description {
  font-size: 0.8125rem;
  color: #64748b;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-clamp: 2;
}
</style>
